import {
  ApiRef,
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@backstage/frontend-plugin-api';
import { BackendAuthApi } from '@internal/plugin-neo4j-auth-common';
import { ResponseError as BackstageResponseError } from '@backstage/errors';
import {
  Configuration,
  DefaultApi,
  ApitypesEnvironmentsCostsResponse,
  ApitypesEnvironmentsCostsSummaryResponse,
} from '@internal/backstage-plugin-neo4j-cloud-costs-common';
import { DateTime } from 'luxon';

export class CloudCostsRestApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;
  private readonly auth: BackendAuthApi;

  constructor(options: {
    discoveryApi: DiscoveryApi;
    fetchApi: FetchApi;
    auth: BackendAuthApi;
  }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
    this.auth = options.auth;
  }

  private async getCloudCostsApi(): Promise<DefaultApi> {
    const baseUrl = await this.discoveryApi.getBaseUrl('proxy');
    const token = await this.auth.getToken();

    const config = new Configuration({
      basePath: `${baseUrl}/cloud-costs-api`,
      fetchApi: this.fetchApi.fetch,
      headers: token ? { 'Aura-Portal-Okta-Token': token } : undefined,
    });

    return new DefaultApi(config);
  }

  async getEnvironmentsCosts(
    startDate: DateTime,
    endDate: DateTime,
    environmentId?: string,
  ): Promise<ApitypesEnvironmentsCostsResponse> {
    try {
      const api = await this.getCloudCostsApi();
      return await api.environmentsGet({
        startDate: startDate.toFormat('yyyy-MM-dd'),
        endDate: endDate.toFormat('yyyy-MM-dd'),
        environmentID: environmentId,
      });
    } catch (error) {
      return this.handleSdkError(error);
    }
  }

  async getEnvironmentsCostsSummary(): Promise<ApitypesEnvironmentsCostsSummaryResponse> {
    try {
      const api = await this.getCloudCostsApi();
      return await api.environmentsSummaryGet();
    } catch (error) {
      return this.handleSdkError(error);
    }
  }

  async exportEnvironmentsCosts(): Promise<Blob> {
    try {
      const api = await this.getCloudCostsApi();
      return await api.environmentsExportGet();
    } catch (error) {
      return this.handleSdkError(error);
    }
  }

  /**
   * Helper function to handle errors from SDK calls
   */
  private async handleSdkError(error: unknown): Promise<never> {
    if (error instanceof BackstageResponseError) {
      throw new Error(
        `API error: ${error.statusCode} ${error.statusText} - ${JSON.stringify(error.body)}`,
      );
    }
    throw error;
  }
}

export const cloudCostsApiRef: ApiRef<CloudCostsRestApi> =
  createApiRef<CloudCostsRestApi>({
    id: 'plugin.neo4j-cloud-costs.service',
  });
